// Tropical fish variants (M18-adjacent). PURE (ENG-1).
//
// Minecraft's tropical fish are one mob wearing 22 named "common" liveries — a
// body pattern plus a base and pattern colour. Modelling them as a variant index
// (rather than 22 mob defs) keeps the registry flat: the mob rolls a variant on
// spawn, the renderer tints its rig from this table, and a bucketed fish carries
// the variant so releasing it gives back the same fish you caught.
export interface TropicalVariant {
  /** The vanilla common-variant name. */
  name: string;
  /** Body pattern — drives which rig part the pattern colour lands on. */
  pattern: 'stripey' | 'flopper' | 'glitter' | 'blockfish' | 'betty' | 'clayfish';
  /** Body colour and pattern colour, as 0xRRGGBB. */
  base: number;
  accent: number;
}

/** The 22 common variants, in a fixed order — the index IS the entity variant. */
export const TROPICAL_VARIANTS: readonly TropicalVariant[] = Object.freeze([
  { name: 'anemone', pattern: 'stripey', base: 0xe08a2a, accent: 0x2a4fa0 },
  { name: 'black_tang', pattern: 'flopper', base: 0x22242c, accent: 0xe0d040 },
  { name: 'blue_tang', pattern: 'flopper', base: 0x1f4fb0, accent: 0xf0e050 },
  { name: 'butterflyfish', pattern: 'flopper', base: 0xf0ece0, accent: 0xe0a020 },
  { name: 'cichlid', pattern: 'blockfish', base: 0x6a3fa0, accent: 0x7fd0e0 },
  { name: 'clownfish', pattern: 'clayfish', base: 0xe86a18, accent: 0xf5f0e6 },
  { name: 'cotton_candy_betta', pattern: 'betty', base: 0xf07ab0, accent: 0x7fd0e0 },
  { name: 'dottyback', pattern: 'blockfish', base: 0x8a3fb0, accent: 0xf0d040 },
  { name: 'emperor_red_snapper', pattern: 'clayfish', base: 0xf0f0f0, accent: 0xc02020 },
  { name: 'goatfish', pattern: 'flopper', base: 0xe0a030, accent: 0xf5f0e6 },
  { name: 'goldfish', pattern: 'blockfish', base: 0xe86a18, accent: 0xf0f0f0 },
  { name: 'moorish_idol', pattern: 'glitter', base: 0xf0ece0, accent: 0x22242c },
  { name: 'ornate_butterflyfish', pattern: 'flopper', base: 0xf0ece0, accent: 0xe86a18 },
  { name: 'parrotfish', pattern: 'stripey', base: 0x2fa0c0, accent: 0xf07ab0 },
  { name: 'queen_angelfish', pattern: 'glitter', base: 0x2fa0c0, accent: 0xf0d040 },
  { name: 'red_cichlid', pattern: 'blockfish', base: 0xc02020, accent: 0x4fb040 },
  { name: 'red_lipped_blenny', pattern: 'glitter', base: 0x5a6a4a, accent: 0xc02020 },
  { name: 'red_snapper', pattern: 'blockfish', base: 0xc02020, accent: 0xf0f0f0 },
  { name: 'threadfin', pattern: 'flopper', base: 0xf0ece0, accent: 0xe0a030 },
  { name: 'tomato_clownfish', pattern: 'clayfish', base: 0xd83020, accent: 0xf5f0e6 },
  { name: 'triggerfish', pattern: 'stripey', base: 0x8a8a8a, accent: 0x22242c },
  { name: 'yellowtail_parrotfish', pattern: 'stripey', base: 0x2fa0c0, accent: 0xf0d040 },
]);

export const TROPICAL_VARIANT_COUNT = TROPICAL_VARIANTS.length;

/** The variant at `index`, wrapping so an out-of-range value is always valid. */
export function tropicalVariant(index: number): TropicalVariant {
  const i = ((Math.floor(index) % TROPICAL_VARIANT_COUNT) + TROPICAL_VARIANT_COUNT) % TROPICAL_VARIANT_COUNT;
  return TROPICAL_VARIANTS[i]!;
}

/** Pick a variant index from a [0,1) roll (the spawn path's rng). */
export function rollTropicalVariant(roll: number): number {
  return Math.min(TROPICAL_VARIANT_COUNT - 1, Math.floor(roll * TROPICAL_VARIANT_COUNT));
}

/**
 * Rig-part tints for a variant: the body always takes the base colour, and the
 * pattern decides where the accent lands — a stripe down the flank, the tail, or
 * both. Everything else stays untinted.
 */
export function tropicalTints(index: number): { body: number; stripe: number; tail: number } {
  const v = tropicalVariant(index);
  switch (v.pattern) {
    case 'stripey':
      return { body: v.base, stripe: v.accent, tail: v.base };
    case 'flopper':
      return { body: v.base, stripe: v.base, tail: v.accent };
    case 'glitter':
      return { body: v.accent, stripe: v.base, tail: v.accent };
    case 'blockfish':
      return { body: v.base, stripe: v.accent, tail: v.accent };
    case 'betty':
      return { body: v.accent, stripe: v.accent, tail: v.base };
    case 'clayfish':
    default:
      return { body: v.base, stripe: v.accent, tail: v.base };
  }
}
